import { forwardRef, useEffect, useState } from 'react';
import { css } from '@emotion/react';

import { isNonEmpty } from '@toolbox/utils';
import { makeStyles } from '@toolbox/style';
import { StyledTextArea } from './StyledTextArea';
import { InputLabel } from './InputLabel';
import { InputContainer } from './InputContainer';
import { InputError } from './InputError';

import type React from 'react';
import type { ReactChild, ReactNode, TextareaHTMLAttributes } from 'react';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  error?: ReactChild | boolean | null;
  keepErrorSpace?: boolean;
};

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    text-align: left;
  `,
  textareaLabel: css`
    top: ${theme.spacing(6)};
    transform: translateY(0);
  `,
  active: css`
    border-color: ${theme.palette.colors.primary};
  `,
  error: css`
    border-color: ${theme.palette.colors.error};
  `,
  disabled: css`
    opacity: 0.5;
    border-color: ${theme.palette.colors.gray[400]};
    &:hover {
      border-color: ${theme.palette.colors.gray[400]};
    }
  `,
  activeLabel: css`
    top: 0;
    transform: translate(-${theme.spacing(1)}, -50%);
    color: ${theme.palette.colors.primary};
    padding: ${theme.spacing(1)};
    font-size: ${theme.fontSizes.f1};
    line-height: ${theme.lineHeights.xs};
  `,
  wrapper: css`
    height: auto;
  `,
}));

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  props,
  ref,
) {
  const {
    keepErrorSpace,
    value,
    defaultValue,
    label,
    error,
    className,
    onFocus,
    onBlur,
    ...otherProps
  } = props;
  const classes = useStyles();
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(() => isNonEmpty(value) || isNonEmpty(defaultValue));

  useEffect(() => {
    if (!focused) {
      setActive(isNonEmpty(value));
    }
  }, [value, focused]);

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    setFocused(true);
    setActive(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    setFocused(false);
    if (!isNonEmpty(e.target.value)) {
      setActive(false);
    }
    onBlur?.(e);
  };

  return (
    <div css={classes.root as any}>
      <InputContainer
        css={
          [
            classes.wrapper,
            active && classes.active,
            error && classes.error,
            props.disabled && classes.disabled,
          ] as any
        }
      >
        {label && (
          <InputLabel css={[classes.textareaLabel, active && classes.activeLabel] as any}>
            {label}
          </InputLabel>
        )}
        <StyledTextArea
          {...otherProps}
          value={value === null ? '' : value}
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </InputContainer>
      {((error && error !== true) || keepErrorSpace) && (
        <InputError>{error !== true && error}</InputError>
      )}
    </div>
  );
});
